import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Loader2, CheckCircle2, XCircle, AlertTriangle, Send } from "lucide-react";
import { useAdminAuth, getAdminAuthHeaders } from "./AdminAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ConfigRow({ label, value, expected, ok }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500 shrink-0">{label}</span>
      <span className={`text-sm font-mono text-right break-all ${ok === false ? "text-amber-600 font-semibold" : "text-slate-900"}`}>
        {value}
        {ok === false && expected && (
          <span className="block text-xs text-amber-600 font-sans font-normal mt-0.5">esperado: {expected}</span>
        )}
      </span>
    </div>
  );
}

export default function AdminEmailDiagnosticsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [configLoading, setConfigLoading] = useState(false);
  const [configResult, setConfigResult] = useState(null);
  const [configError, setConfigError] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState("");

  // Frontend-only gate (temporary diagnostic tool) — the backend endpoints are
  // already restricted to admin/super_admin roles; this additionally hides and
  // blocks the page itself for anything other than super_admin, per request.
  React.useEffect(() => {
    if (user && user.role !== "super_admin") navigate("/admin", { replace: true });
  }, [user, navigate]);
  if (!user || user.role !== "super_admin") return null;

  const checkConfig = async () => {
    setConfigLoading(true); setConfigError(""); setConfigResult(null);
    try {
      const res = await axios.get(`${API}/admin/diagnostics/email-config`, { headers: getAdminAuthHeaders() });
      setConfigResult(res.data);
    } catch (err) {
      setConfigError(err.response?.data?.detail || "No se pudo consultar la configuración.");
    } finally {
      setConfigLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setTestLoading(true); setTestError(""); setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/diagnostics/test-email`, {}, { headers: getAdminAuthHeaders() });
      setTestResult(res.data);
    } catch (err) {
      setTestError(err.response?.data?.detail || "No se pudo enviar el email de prueba.");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div data-testid="admin-email-diagnostics-page" className="max-w-xl mx-auto pb-8">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-900 mb-1">Diagnóstico de Email</h1>
        <p className="text-sm text-slate-500">Herramienta temporal — solo super_admin. Úsala para confirmar por qué los emails de recuperación no llegan en producción.</p>
      </div>

      {/* ── Sección 1: Configuración ───────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-5 w-5 text-[#028CF8]" />
          <h2 className="font-semibold text-slate-900">Configuración de email</h2>
        </div>
        <button
          data-testid="diag-check-config-button"
          onClick={checkConfig}
          disabled={configLoading}
          className="w-full h-12 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] disabled:opacity-70 text-white font-semibold text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        >
          {configLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Revisar configuración de email"}
        </button>

        {configError && (
          <div data-testid="diag-config-error" className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
            <XCircle className="h-4 w-4 shrink-0" /> {configError}
          </div>
        )}

        {configResult && (
          <div data-testid="diag-config-result" className="mt-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg mb-2 ${configResult.resend_api_key === "present" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {configResult.resend_api_key === "present" ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
              <span className="font-semibold text-sm">RESEND_API_KEY: {configResult.resend_api_key === "present" ? "present" : "MISSING"}</span>
            </div>
            <div className="px-1">
              <ConfigRow label="EMAIL_FROM" value={configResult.email_from} />
              <ConfigRow label="EMAIL_REPLY_TO" value={configResult.email_reply_to} />
              <ConfigRow label="PUBLIC_APP_URL" value={configResult.public_app_url}
                expected="https://infocityjobs.com" ok={configResult.public_app_url === "https://infocityjobs.com"} />
              <ConfigRow label="ENVIRONMENT" value={configResult.environment}
                expected="production" ok={configResult.environment === "production"} />
            </div>
          </div>
        )}
      </div>

      {/* ── Sección 2: Email de prueba ─────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Send className="h-5 w-5 text-[#028CF8]" />
          <h2 className="font-semibold text-slate-900">Email de prueba</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Se envía únicamente a tu propia cuenta ({user.email}). No podés elegir otro destinatario.</p>
        <button
          data-testid="diag-send-test-email-button"
          onClick={sendTestEmail}
          disabled={testLoading}
          className="w-full h-12 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] disabled:opacity-70 text-white font-semibold text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        >
          {testLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar email de prueba"}
        </button>

        {testError && (
          <div data-testid="diag-test-error" className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
            <XCircle className="h-4 w-4 shrink-0" /> {testError}
          </div>
        )}

        {testResult && (
          <div data-testid="diag-test-result" className="mt-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg mb-2 ${testResult.sent ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {testResult.sent ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
              <span className="font-semibold text-sm">Enviado: {testResult.sent ? "Sí" : "No"}</span>
            </div>
            <div className="px-1">
              <ConfigRow label="Destino" value={testResult.to} />
              <ConfigRow label="Message ID" value={testResult.message_id || "—"} />
              <ConfigRow label="Error" value={testResult.error || "ninguno"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
